import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import useSpeech, { speak, cancelSpeech } from '../hooks/useSpeech';
import { parseCommand } from '../utils/voiceCommands';
import MessageContent, { stripMarkdown } from './MessageContent';
import { LogoMark } from './Logo';
import { MemberDetail, getAvatarStyle, Avatar } from './DetailViews';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: '⬡', exact: true },
  { to: '/app/leaderboard', label: 'Leaderboard', icon: '🏆' },
  { to: '/app/quests', label: 'Quest Board', icon: '⚡' },
  { to: '/app/contributions', label: 'Contributions', icon: '📊' },
  { to: '/app/library', label: 'Use Case Library', icon: '📚' },
  { to: '/app/pods', label: 'Pods', icon: '🔬' },
  { to: '/app/interns', label: 'Intern Program', icon: '🎓' },
  { to: '/app/faq', label: 'FAQ', icon: '❓' },
  { to: '/app/messages', label: 'Messages', icon: '💬' },
  { to: '/app/events', label: 'Community', icon: '🎉' },
  { to: '/app/trends', label: 'My Analytics', icon: '📈' },
];
const ADMIN_NAV = [
  { to: '/app/members',  label: 'Members',  icon: '👥' },
  { to: '/app/waitlist', label: 'Waitlist',  icon: '📋', roles: ['cwg'] },
  { to: '/app/reports',  label: 'Reports',   icon: '📑' },
];

function formatMsgTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isYesterday = d.toDateString() === new Date(now - 86400000).toDateString();
  const t = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return t;
  if (isYesterday) return `Yesterday ${t}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${t}`;
}

function formatChatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date(now - 86400000).toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const makeWelcome = useCallback(() => ({
    role: 'assistant',
    content: `Hello ${user?.name?.split(' ')[0] || 'there'}! I'm JARVIS, your NeuralOps AI assistant. Ask me anything about the programme — points, tiers, quests, pods — or say "open the leaderboard" to navigate.`,
    ts: new Date().toISOString(),
  }), [user]);

  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState(() => [makeWelcome()]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [dmUnread, setDmUnread] = useState(0);
  const [waitlistPending, setWaitlistPending] = useState(0);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    const refresh = () => {
      api.get('/messages/contacts', { silent: true })
        .then(r => setDmUnread(r.data.reduce((s, c) => s + (c.unreadCount || 0), 0)))
        .catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!['cwg', 'stakeholder'].includes(user?.role)) return;
    const refresh = () => {
      api.get('/waitlist', { silent: true })
        .then(r => setWaitlistPending(r.data.filter(w => w.status === 'pending').length))
        .catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [user?.role]);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const isAdmin = ['cwg', 'stakeholder', 'sme'].includes(user?.role);

  const loadChatList = useCallback(() => {
    api.get('/ai/chats', { silent: true }).then(r => setChats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (chatOpen) { loadChatList(); setTimeout(() => inputRef.current?.focus(), 120); }
  }, [chatOpen, loadChatList]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  useEffect(() => () => cancelSpeech(), []);

  const [errorToast, setErrorToast] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      setErrorToast(e.detail?.message || 'Something went wrong.');
      setTimeout(() => setErrorToast(null), 4000);
    };
    window.addEventListener('api-error', handler);
    return () => window.removeEventListener('api-error', handler);
  }, []);

  const openNotifications = () => {
    const next = !showNotifs;
    setShowNotifs(next);
    if (next && unread > 0) {
      api.patch('/notifications/read-all', null, { silent: true }).catch(() => {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  const startNewChat = useCallback(() => {
    cancelSpeech();
    setActiveSessionId(null);
    setMessages([makeWelcome()]);
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [makeWelcome]);

  const openSession = useCallback(async (id) => {
    if (id === activeSessionId) return;
    setLoadingSession(true);
    try {
      const { data } = await api.get(`/ai/chats/${id}`);
      cancelSpeech();
      setActiveSessionId(data.id);
      setMessages(data.messages.length ? data.messages : [makeWelcome()]);
    } catch { /* global toast */ } finally {
      setLoadingSession(false);
    }
  }, [activeSessionId, makeWelcome]);

  const deleteSession = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/chats/${id}`);
      setChats(prev => prev.filter(c => c.id !== id));
      if (activeSessionId === id) startNewChat();
    } catch { /* global toast */ }
  }, [activeSessionId, startNewChat]);

  const respond = useCallback((text, spoken) => {
    setMessages(prev => [...prev, { role: 'assistant', content: text, ts: new Date().toISOString() }]);
    if (spoken) speak(stripMarkdown(text), { onStart: () => setSpeaking(true), onEnd: () => setSpeaking(false) });
  }, []);

  const handleUserMessage = useCallback(async (text, { spoken = false } = {}) => {
    const content = (text || '').trim();
    if (!content) return;
    setChatOpen(true);
    setMessages(prev => [...prev, { role: 'user', content, ts: new Date().toISOString() }]);

    const cmd = parseCommand(content, { isAdmin });
    if (cmd) {
      if (cmd.type === 'navigate') { navigate(cmd.path); respond(`Right away — taking you to ${cmd.label}.`, spoken); return; }
      if (cmd.type === 'logout') { respond('Logging you out. Goodbye.', spoken); setTimeout(() => { logout(); navigate('/login'); }, 900); return; }
      if (cmd.type === 'closeChat') { cancelSpeech(); setChatOpen(false); return; }
    }

    setTyping(true);
    try {
      const { data } = await api.post('/ai/chat', { sessionId: activeSessionId, message: content });
      if (data.sessionId && data.sessionId !== activeSessionId) setActiveSessionId(data.sessionId);
      respond(data.response, spoken);
      if (data.sessionId) loadChatList();
    } catch {
      respond('I encountered an error. Please try again.', spoken);
    }
    setTyping(false);
  }, [isAdmin, navigate, logout, respond, activeSessionId, loadChatList]);

  const { supported: voiceSupported, listening, start: startListening, stop: stopListening, micError, clearMicError } =
    useSpeech({ onResult: text => handleUserMessage(text, { spoken: true }) });

  const sendChat = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setInput('');
    handleUserMessage(value, { spoken: false });
  };

  const toggleMic = () => {
    if (!voiceSupported) return;
    setChatOpen(true);
    if (listening) stopListening(); else startListening();
  };

  const unread = notifications.filter(n => !n.read).length;
  const navItems = (isAdmin ? [...NAV, ...ADMIN_NAV] : NAV).filter(n => !n.roles || n.roles.includes(user?.role));
  const roleLabel = { cwg: 'CWG', stakeholder: 'STAKEHOLDER', sme: 'SME', member: 'MEMBER' }[user?.role] || 'MEMBER';

  const activeChat = chats.find(c => c.id === activeSessionId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)' }}>
      {errorToast && (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, background: 'rgba(40,6,6,0.97)', border: '1px solid var(--j6)', color: '#ffd9d9', padding: '10px 18px', borderRadius: 4, fontSize: 12, fontFamily: 'Share Tech Mono', boxShadow: '0 0 24px rgba(255,60,60,0.25)' }}>
          ⚠ {errorToast}
        </div>
      )}

      {/* Topbar */}
      <div style={{ height: 52, background: 'rgba(2,4,8,0.97)', borderBottom: '1px solid var(--brd2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', flexShrink: 0, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 800, color: 'var(--j1)', textShadow: '0 0 15px var(--glow1)', letterSpacing: 3 }}>
            NEURAL<span style={{ color: 'var(--txt2)' }}>OPS</span>
          </div>
          <div style={{ fontSize: 9, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 2 }}>AI CENTER OF EXCELLENCE</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 2, fontSize: 9, color: 'var(--j5)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--j5)', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            SYSTEMS ONLINE
          </div>
          {voiceSupported && (
            <button onClick={toggleMic} title={listening ? 'Stop' : 'Talk to JARVIS'} style={{ background: listening ? 'rgba(255,107,53,0.15)' : 'none', border: `1px solid ${listening ? 'var(--j3)' : 'var(--brd2)'}`, borderRadius: 2, padding: '4px 10px', cursor: 'pointer', color: listening ? 'var(--j3)' : speaking ? 'var(--j1)' : 'var(--txt2)', fontFamily: 'Share Tech Mono', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6, animation: listening ? 'pulse 1.2s infinite' : 'none' }}>
              {listening ? '🎙️ LISTENING' : speaking ? '🔊 JARVIS' : '🎤 VOICE'}
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button onClick={openNotifications} style={{ background: 'none', border: '1px solid var(--brd2)', borderRadius: 2, padding: '4px 10px', color: 'var(--txt2)', cursor: 'pointer', fontFamily: 'Share Tech Mono', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
              🔔 {unread > 0 && <span style={{ background: 'var(--j6)', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{unread}</span>}
            </button>
            {showNotifs && (
              <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--bg3)', border: '1px solid var(--brd)', borderRadius: 4, width: 320, maxHeight: 300, overflowY: 'auto', zIndex: 999 }}>
                {notifications.length === 0 ? <div className="empty-state" style={{ padding: 16 }}>No notifications</div> :
                  notifications.map(n => (
                    <div key={n.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--brd2)', fontSize: 12, color: n.read ? 'var(--txt2)' : 'var(--txt)', background: n.read ? 'transparent' : 'rgba(0,212,255,0.03)' }}>
                      <div>{n.message}</div>
                      <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 3 }}>{new Date(n.createdAt).toLocaleDateString()}</div>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
          <button onClick={() => setShowProfile(true)} title="View my profile"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', border: '1px solid var(--brd2)', borderRadius: 2, background: 'rgba(0,212,255,0.03)', cursor: 'pointer' }}>
            <Avatar user={user} size={26} />
            <span style={{ fontSize: 11, fontFamily: 'Share Tech Mono', color: 'var(--txt2)', letterSpacing: 1 }}>{user?.name?.split(' ')[0]}</span>
            <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 2, background: 'rgba(255,107,53,0.12)', color: 'var(--j3)', border: '1px solid rgba(255,107,53,0.25)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>{roleLabel}</span>
          </button>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn btn-danger btn-sm">EXIT</button>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,var(--j1),var(--j2),transparent)' }} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '200px 1fr', overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{ background: 'rgba(2,4,8,0.98)', borderRight: '1px solid var(--brd2)', overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0 10px 16px', borderBottom: '1px solid var(--brd2)', marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--txt3)', letterSpacing: 2, fontFamily: 'Share Tech Mono', marginBottom: 6, paddingLeft: 6 }}>ACTIVE MEMBER</div>
            <div style={{ padding: '8px 10px', background: 'rgba(0,212,255,0.03)', border: '1px solid var(--brd2)', borderRadius: 3 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>{user?.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span className={`tier-badge tier-${user?.tier?.toLowerCase()}`}>{user?.tier}</span>
                <span style={{ fontSize: 11, color: 'var(--j1)', fontFamily: 'Orbitron', fontWeight: 700 }}>{(user?.totalPoints || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 10px', flex: 1 }}>
            <div style={{ fontSize: 8, color: 'var(--txt3)', letterSpacing: 3, fontFamily: 'Share Tech Mono', paddingLeft: 6, marginBottom: 8, textTransform: 'uppercase' }}>Navigation</div>
            {navItems.map(n => (
              <NavLink key={n.to} to={n.to} end={n.exact} style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 2, textDecoration: 'none', fontSize: 12, fontFamily: 'Rajdhani', fontWeight: 600, letterSpacing: 0.5, marginBottom: 2, border: '1px solid', color: isActive ? 'var(--j1)' : 'var(--txt2)', background: isActive ? 'rgba(0,212,255,0.06)' : 'transparent', borderColor: isActive ? 'var(--brd)' : 'transparent', boxShadow: isActive ? '0 0 8px rgba(0,212,255,0.1)' : 'none', transition: 'all 0.15s' })}>
                <span>{n.icon}</span>
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.to === '/app/messages' && dmUnread > 0 && (
                  <span style={{ background: 'var(--j6)', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{dmUnread > 9 ? '9+' : dmUnread}</span>
                )}
                {n.to === '/app/waitlist' && waitlistPending > 0 && (
                  <span style={{ background: '#ffd700', color: '#000', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{waitlistPending > 9 ? '9+' : waitlistPending}</span>
                )}
              </NavLink>
            ))}
          </div>
          <div style={{ padding: '16px 10px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => setShowProfile(true)}
              style={{ width: '100%', padding: '8px 10px', background: 'transparent', border: '1px solid var(--brd2)', borderRadius: 3, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', fontSize: 11, letterSpacing: 1, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar user={user} size={20} />
              MY PROFILE
            </button>
            <button onClick={() => setChatOpen(true)} style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px solid var(--brd)', borderRadius: 3, color: 'var(--j1)', fontFamily: 'Orbitron', fontSize: 11, fontWeight: 700, letterSpacing: 2, cursor: 'pointer', textTransform: 'uppercase' }}>
              ⬡ ASK JARVIS
            </button>
          </div>
        </div>

        {/* Main content */}
        <div style={{ overflowY: 'auto', padding: 24 }}>
          <Outlet />
        </div>
      </div>

      {/* ── Floating launcher FAB (visible whenever chat is closed) ── */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          title="Ask JARVIS"
          style={{
            position: 'fixed', bottom: 28, right: 28,
            width: 58, height: 58, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(0,212,255,0.15), rgba(0,153,255,0.08))',
            border: '1px solid var(--j1)', color: 'var(--j1)',
            fontSize: 26, cursor: 'pointer', zIndex: 9000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 28px rgba(0,212,255,0.4), 0 4px 16px rgba(0,0,0,0.5)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(0,212,255,0.6), 0 4px 20px rgba(0,0,0,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 28px rgba(0,212,255,0.4), 0 4px 16px rgba(0,0,0,0.5)'; }}
        >
          ⬡
        </button>
      )}

      {/* ── JARVIS Chat Window ── */}
      {showProfile && user?.id && (
        <MemberDetail id={user.id} onClose={() => setShowProfile(false)} />
      )}

      {chatOpen && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          width: 700, height: 560,
          background: 'var(--bg3)',
          border: '1px solid var(--brd)',
          borderRadius: 6,
          display: 'flex', flexDirection: 'column',
          zIndex: 9999,
          boxShadow: '0 0 60px rgba(0,212,255,0.12), 0 20px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}>

          {/* Header */}
          <div style={{ height: 52, padding: '0 16px', borderBottom: '1px solid var(--brd2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,212,255,0.03)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--j1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, background: 'rgba(0,212,255,0.1)', boxShadow: '0 0 12px rgba(0,212,255,0.2)' }}>⬡</div>
              <div>
                <div style={{ fontFamily: 'Orbitron', fontSize: 13, fontWeight: 700, color: 'var(--j1)', letterSpacing: 2 }}>JARVIS</div>
                <div style={{ fontSize: 9, color: listening ? 'var(--j3)' : 'var(--j5)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
                  {listening ? '● LISTENING…' : speaking ? '● SPEAKING…' : '● ONLINE — NeuralOps AI'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={startNewChat}
                title="New chat"
                style={{ background: 'transparent', border: '1px solid var(--brd2)', borderRadius: 3, color: 'var(--txt2)', cursor: 'pointer', fontSize: 10, padding: '4px 10px', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}
              >
                + NEW
              </button>
              {voiceSupported && (
                <button onClick={toggleMic} title={listening ? 'Stop' : 'Voice'} style={{ background: listening ? 'rgba(255,107,53,0.15)' : 'transparent', border: `1px solid ${listening ? 'var(--j3)' : 'var(--brd2)'}`, borderRadius: 3, color: listening ? 'var(--j3)' : 'var(--txt2)', cursor: 'pointer', fontSize: 14, padding: '4px 8px' }}>
                  {listening ? '⏹' : '🎙️'}
                </button>
              )}
              <button onClick={() => setChatOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--txt2)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          </div>

          {/* Two-pane body */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

            {/* ── Left: Chat list ── */}
            <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--brd2)', display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.2)' }}>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* New / current chat entry */}
                <div
                  onClick={startNewChat}
                  style={{ padding: '10px 12px', borderBottom: '1px solid var(--brd2)', cursor: 'pointer', background: !activeSessionId ? 'rgba(0,212,255,0.06)' : 'transparent', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,212,255,0.1)', border: '1px solid var(--brd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>⬡</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: !activeSessionId ? 'var(--j1)' : 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>New Chat</div>
                    <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Ask JARVIS anything</div>
                  </div>
                </div>

                {/* Past sessions */}
                {chats.length === 0 && (
                  <div style={{ padding: '20px 12px', textAlign: 'center', fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', lineHeight: 1.8 }}>
                    No past chats yet.<br />Start a conversation!
                  </div>
                )}
                {chats.map(c => (
                  <div
                    key={c.id}
                    onClick={() => openSession(c.id)}
                    style={{ padding: '10px 12px', borderBottom: '1px solid var(--brd2)', cursor: 'pointer', background: c.id === activeSessionId ? 'rgba(0,212,255,0.06)' : 'transparent', display: 'flex', alignItems: 'flex-start', gap: 8, position: 'relative' }}
                    onMouseEnter={e => { if (c.id !== activeSessionId) e.currentTarget.style.background = 'rgba(0,212,255,0.03)'; e.currentTarget.querySelector('.del-btn').style.opacity = '1'; }}
                    onMouseLeave={e => { if (c.id !== activeSessionId) e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.del-btn').style.opacity = '0'; }}
                  >
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,212,255,0.08)', border: `1px solid ${c.id === activeSessionId ? 'var(--j1)' : 'var(--brd2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, color: 'var(--j1)' }}>⬡</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginBottom: 2 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: c.id === activeSessionId ? 'var(--j1)' : 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{c.title || 'Chat'}</div>
                        <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', flexShrink: 0 }}>{formatChatTime(c.updatedAt)}</div>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{c.messageCount} message{c.messageCount !== 1 ? 's' : ''}</div>
                    </div>
                    <button
                      className="del-btn"
                      onClick={e => deleteSession(c.id, e)}
                      title="Delete"
                      style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,45,85,0.1)', border: '1px solid rgba(255,45,85,0.25)', borderRadius: 3, color: 'var(--j6)', cursor: 'pointer', fontSize: 10, padding: '2px 5px', opacity: 0, transition: 'opacity 0.15s' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: Conversation ── */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

              {/* Chat title bar */}
              <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--brd2)', flexShrink: 0, background: 'rgba(0,0,0,0.15)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--txt)', fontFamily: 'Share Tech Mono', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {activeSessionId && activeChat ? activeChat.title : 'New Conversation'}
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {loadingSession ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--txt3)', fontFamily: 'Share Tech Mono', fontSize: 11 }}>Loading…</div>
                ) : messages.map((m, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', gap: 4 }}>
                    {m.role === 'assistant' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 2 }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,212,255,0.1)', border: '1px solid var(--brd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--j1)' }}>⬡</div>
                        <span style={{ fontSize: 10, color: 'var(--j1)', fontFamily: 'Share Tech Mono', fontWeight: 600, letterSpacing: 1 }}>JARVIS</span>
                      </div>
                    )}
                    <div className={`chat-bubble ${m.role === 'user' ? 'chat-user' : 'chat-bot'}`} style={{ maxWidth: '78%' }}>
                      {m.role === 'user' ? m.content : <MessageContent content={m.content} />}
                    </div>
                    {m.ts && (
                      <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', paddingLeft: m.role === 'user' ? 0 : 2, paddingRight: m.role === 'user' ? 2 : 0 }}>
                        {formatMsgTime(m.ts)}
                      </div>
                    )}
                  </div>
                ))}

                {typing && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 2 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,212,255,0.1)', border: '1px solid var(--brd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--j1)' }}>⬡</div>
                      <span style={{ fontSize: 10, color: 'var(--j1)', fontFamily: 'Share Tech Mono', fontWeight: 600, letterSpacing: 1 }}>JARVIS</span>
                    </div>
                    <div className="chat-bubble chat-bot" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 14px' }}>
                      {[0, 1, 2].map(n => (
                        <span key={n} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--j1)', display: 'inline-block', opacity: 0.4, animation: `pulse 1.2s ${n * 0.2}s infinite` }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Quick prompts */}
              <div style={{ padding: '6px 12px', display: 'flex', gap: 4, flexWrap: 'wrap', borderTop: '1px solid var(--brd2)', background: 'rgba(0,0,0,0.1)' }}>
                {['How do I earn points?', 'What are the tiers?', 'Open leaderboard', 'Go to quests'].map(p => (
                  <button
                    key={p}
                    onClick={() => { setInput(p); inputRef.current?.focus(); }}
                    style={{ padding: '3px 9px', background: 'transparent', border: '1px solid var(--brd2)', borderRadius: 12, fontSize: 10, color: 'var(--txt2)', cursor: 'pointer', fontFamily: 'Share Tech Mono', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--j1)'; e.currentTarget.style.color = 'var(--j1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--brd2)'; e.currentTarget.style.color = 'var(--txt2)'; }}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* Mic error banner */}
              {micError && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,60,60,0.1)', borderTop: '1px solid rgba(255,60,60,0.3)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>🎤</span>
                  <span style={{ fontSize: 11, color: '#ffaaaa', fontFamily: 'Share Tech Mono', lineHeight: 1.5, flex: 1 }}>{micError}</span>
                  <button onClick={clearMicError} style={{ background: 'none', border: 'none', color: '#ffaaaa', cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0 }}>×</button>
                </div>
              )}
              {/* Input bar */}
              <form onSubmit={sendChat} style={{ padding: '10px 12px', borderTop: '1px solid var(--brd2)', display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(0,0,0,0.15)' }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={listening ? 'Listening…' : 'Message JARVIS…'}
                  className="input"
                  style={{ fontSize: 13, flex: 1, borderRadius: 20, padding: '9px 16px' }}
                  disabled={typing || loadingSession}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(e); } }}
                />
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={toggleMic}
                    title={listening ? 'Stop' : 'Voice'}
                    style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, border: `1px solid ${listening ? 'var(--j3)' : 'var(--brd)'}`, background: listening ? 'rgba(255,107,53,0.15)' : 'transparent', color: listening ? 'var(--j3)' : 'var(--txt2)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: listening ? 'pulse 1.2s infinite' : 'none' }}
                  >
                    {listening ? '⏹' : '🎙️'}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={typing || loadingSession || !input.trim()}
                  style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, border: '1px solid var(--j1)', background: input.trim() ? 'rgba(0,212,255,0.15)' : 'transparent', color: 'var(--j1)', cursor: input.trim() ? 'pointer' : 'default', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', opacity: input.trim() ? 1 : 0.4 }}
                >
                  ➤
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
